export type AppDepartment =
  | 'cultureTalent'
  | 'sales'
  | 'services'
  | 'development'
  | 'reporting'
  | 'operations'
  | 'tools'
  | 'communication';

export const DEPARTMENT_LABELS: Record<AppDepartment, string> = {
  cultureTalent: 'Culture and Talent',
  sales: 'Sales',
  services: 'Services',
  development: 'Development',
  reporting: 'Reporting',
  operations: 'Operations',
  tools: 'Tools',
  communication: 'Chat',
};

export const DEPARTMENT_ORDER: AppDepartment[] = [
  'cultureTalent',
  'sales',
  'services',
  'development',
  'reporting',
  'operations',
  'tools',
  'communication',
];

export const DEPARTMENT_ICONS: Record<AppDepartment, string> = {
  cultureTalent: 'People',
  sales: 'Money',
  services: 'Clock',
  development: 'DeveloperTools',
  reporting: 'ReportDocument',
  operations: 'Shop',
  tools: 'Settings',
  communication: 'Comment',
};

export type ToolsSubgroup = 'connectwise' | 'security' | 'documentation';

export const TOOLS_SUBGROUP_LABELS: Record<ToolsSubgroup, string> = {
  connectwise: 'ConnectWise Administration',
  security: 'Security & Credentials',
  documentation: 'Documentation & Feedback',
};

export const TOOLS_SUBGROUP_ORDER: ToolsSubgroup[] = [
  'connectwise',
  'security',
  'documentation',
];

export interface AppCatalogNavChild {
  id: string;
  title: string;
  route: string;
  icon: string;
  /** Permission ids that grant this child. Empty/omitted inherits the parent entry. */
  permissions?: string[];
}

export interface AppCatalogEntry {
  id: string;
  title: string;
  description: string;
  route: string;
  icon: string;
  department: AppDepartment;
  toolsSubgroup?: ToolsSubgroup;
  /** Permission ids that grant access. Empty means misconfigured, not open. */
  permissions: string[];
  quickAccess?: boolean;
  sortOrder?: number;
  navChildren?: AppCatalogNavChild[];
  /** When true, entry is a nav group header only (no direct navigation). */
  navGroupOnly?: boolean;
  /** Optional alternate label for the nav submenu parent. */
  navTitle?: string;
}

export const APP_CATALOG: AppCatalogEntry[] = [
  // Culture and Talent
  {
    id: 'absenceRequests',
    title: 'Absence Requests',
    description: 'View and submit absence requests',
    route: '/absence-request',
    icon: 'Calendar',
    department: 'cultureTalent',
    permissions: ['absence.view'],
    quickAccess: true,
    sortOrder: 10,
    navChildren: [
      { id: 'viewRequests', title: 'View Requests', route: '/absence-request', icon: 'View' },
      { id: 'newRequest', title: 'New Request', route: '/absence-request', icon: 'Add' },
    ],
  },
  {
    id: 'employeeForms',
    title: 'Employee Forms',
    description: 'Onboard and offboard employees',
    route: '/employee-forms',
    icon: 'ContactCard',
    department: 'cultureTalent',
    permissions: ['employeeforms.submit'],
    sortOrder: 20,
    navGroupOnly: true,
    navTitle: 'Employees',
    navChildren: [
      { id: 'employeeFormsList', title: 'Employee Forms', route: '/employee-forms', icon: 'ContactCard' },
      { id: 'addEmployee', title: 'Add Employee', route: '/employee-forms', icon: 'AddFriend' },
      { id: 'termEmployee', title: 'Term Employee', route: '/employee-forms', icon: 'UserRemove' },
      { id: 'updateEmployee', title: 'Update Employee', route: '/employee-update', icon: 'EditContact' },
    ],
  },
  {
    id: 'employeeUpdate',
    title: 'Employee Update Form',
    description: 'Update your personal information, address, and emergency contacts',
    route: '/employee-update',
    icon: 'EditContact',
    department: 'cultureTalent',
    permissions: ['employeeupdate.self'],
    quickAccess: true,
    sortOrder: 30,
  },
  {
    id: 'documentAcceptance',
    title: 'Document Acceptance',
    description: 'Manage document uploads and track employee acceptance',
    route: '/acceptancemanager',
    icon: 'DocumentApproval',
    department: 'cultureTalent',
    permissions: ['documentacceptance.manage'],
    sortOrder: 40,
  },

  // Sales
  {
    id: 'salesDashboard',
    title: 'Sales Dashboard',
    description: 'View sales revenue, orders, and opportunities by territory',
    route: '/sales-dashboard',
    icon: 'Money',
    department: 'sales',
    permissions: ['salesdashboard.view'],
    sortOrder: 10,
  },
  {
    id: 'salesHeatMap',
    title: 'Sales Heat Map',
    description: 'Track technology adoption and awareness across customers',
    route: '/salesheatmap',
    icon: 'WorldClock',
    department: 'sales',
    permissions: ['salesheatmap.view'],
    sortOrder: 20,
  },
  {
    id: 'renewalDashboard',
    title: 'Renewal Dashboard',
    description:
      'Track upcoming agreement and configuration renewals; projections available to Developer, COO, and Sales Director',
    route: '/renewal-dashboard',
    icon: 'DateTime',
    department: 'sales',
    permissions: ['renewaldashboard.view'],
    sortOrder: 25,
  },
  {
    id: 'expiredRenewals',
    title: 'Expired Renewals',
    description: 'Active agreement and configuration renewals that have already expired',
    route: '/renewal-dashboard/expired',
    icon: 'Warning',
    department: 'sales',
    permissions: ['renewaldashboard.view'],
    sortOrder: 26,
  },
  {
    id: 'redGreen',
    title: 'Red/Green Credit Check',
    description: 'Verify opportunity amounts and credit availability',
    route: '/red-green',
    icon: 'Lightbulb',
    department: 'sales',
    permissions: ['redgreen.use'],
    sortOrder: 30,
  },
  {
    id: 'opportunityAnalysis',
    title: 'Opportunity Analysis',
    description: 'Analyze won opportunities with sales cycle metrics and performance data',
    route: '/opportunityanalysis',
    icon: 'BarChartVertical',
    department: 'sales',
    permissions: ['opportunityanalysis.view'],
    sortOrder: 40,
  },
  {
    id: 'hpSalesTracker',
    title: 'HP Sales Tracker Importer',
    description: 'Upload HPE Sales Tracker Excel files for reporting annual sales',
    route: '/hp-sales-tracker',
    icon: 'Upload',
    department: 'sales',
    permissions: ['hpsalestracker.import'],
    sortOrder: 50,
  },
  {
    id: 'hpeBom',
    title: 'HPE BOM Processing',
    description: 'Calculate product line totals for HPE Bill of Materials',
    route: '/hpe-bom',
    icon: 'ExcelDocument',
    department: 'sales',
    permissions: ['hpebom.view'],
    sortOrder: 60,
  },

  // Services
  {
    id: 'serviceTimeAnalysis',
    title: 'Service Time Analysis',
    description: 'Analyze service time utilization and patterns',
    route: '/service-time-analysis',
    icon: 'Clock',
    department: 'services',
    permissions: ['servicetime.view'],
    sortOrder: 10,
  },
  {
    id: 'onCallManager',
    title: 'On-Call Manager',
    description: 'Manage on-call member configuration and notifications',
    route: '/oncall-manager',
    icon: 'Ringer',
    department: 'services',
    permissions: ['oncall.view'],
    quickAccess: true,
    sortOrder: 20,
  },
  {
    id: 'changeControl',
    title: 'Change Control',
    description: 'Submit and manage change control requests and approvals',
    route: '/change-control',
    icon: 'ChangeEntitlements',
    department: 'services',
    permissions: ['changecontrol.view'],
    quickAccess: true,
    sortOrder: 30,
  },
  {
    id: 'backups',
    title: 'Backups',
    description: 'Review daily backup exceptions and enter status',
    route: '/backups/review',
    icon: 'CloudUpload',
    department: 'services',
    permissions: [
      'backups.statusentry',
      'backups.dailyreview',
      'backups.jobdefinitions.view',
      'backups.admin',
      'backups.mismatch.review',
    ],
    sortOrder: 35,
    navChildren: [
      { id: 'backupsReview', title: 'Daily review', route: '/backups/review', icon: 'CheckList', permissions: ['backups.dailyreview'] },
      { id: 'backupsStatus', title: 'Status entry', route: '/backups', icon: 'CalendarWeek', permissions: ['backups.statusentry'] },
      { id: 'backupsCompanyAliases', title: 'Company aliases', route: '/backups/company-aliases', icon: 'Switch', permissions: ['backups.statusentry'] },
      { id: 'backupsParserIssues', title: 'Parser issues', route: '/backups/parser-issues', icon: 'Warning', permissions: ['backups.mismatch.review'] },
      { id: 'backupsJobs', title: 'Job definitions', route: '/backups/jobs', icon: 'Settings', permissions: ['backups.jobdefinitions.view'] },
      { id: 'backupsAnalyze', title: 'Email analysis', route: '/backups/analyze', icon: 'SearchAndApps', permissions: ['backups.admin'] },
    ],
  },
  {
    id: 'eolEos',
    title: 'EOL/EOS Tracking',
    description: 'Track End of Life and End of Support dates for products',
    route: '/eol-eos',
    icon: 'DateTime',
    department: 'services',
    permissions: ['eoleos.view'],
    sortOrder: 40,
  },

  // Development
  {
    id: 'developmentDashboard',
    title: 'Development Dashboard',
    description: 'Weekly EOS metrics for Development board including SLA compliance and ticket stats',
    route: '/development-dashboard',
    icon: 'DeveloperTools',
    department: 'development',
    permissions: ['developmentdashboard.view'],
    sortOrder: 10,
  },
  {
    id: 'devBacklog',
    title: 'Development Backlog',
    description: 'Submit ideas and manage the development work board',
    route: '/dev-backlog',
    icon: 'BulletedList',
    department: 'development',
    permissions: [
      'devbacklog.board.view',
      'devbacklog.ideas.view',
      'devbacklog.ideas.view.own',
      'devbacklog.ideas.submit',
    ],
    sortOrder: 20,
    navChildren: [
      {
        id: 'devBacklogBoard',
        title: 'Board',
        route: '/dev-backlog',
        icon: 'ViewDashboard',
        permissions: ['devbacklog.board.view'],
      },
      {
        id: 'devBacklogIdeas',
        title: 'Ideas',
        route: '/dev-backlog/ideas',
        icon: 'Lightbulb',
        permissions: ['devbacklog.ideas.view', 'devbacklog.ideas.view.own'],
      },
      {
        id: 'devBacklogIntake',
        title: 'Submit idea',
        route: '/dev-backlog/intake',
        icon: 'Add',
        permissions: ['devbacklog.ideas.submit'],
      },
      {
        id: 'devBacklogRejected',
        title: 'Rejected / Deferred',
        route: '/dev-backlog/rejected',
        icon: 'Blocked',
        permissions: ['devbacklog.rejected.view'],
      },
    ],
  },
  {
    id: 'devPriority',
    title: 'Dev Priority',
    description: 'Manage and prioritize development tickets',
    route: '/devpriority',
    icon: 'FavoriteStarFill',
    department: 'development',
    permissions: ['devpriority.manage'],
    sortOrder: 30,
  },
  {
    id: 'estimateActionPlan',
    title: 'Estimate Action Plan',
    description: 'Manage tasks and schedules for approved project estimates',
    route: '/estimate-action-plan',
    icon: 'TaskManager',
    department: 'development',
    permissions: ['estimateactionplan.view'],
    sortOrder: 40,
  },

  // Reporting
  {
    id: 'reports',
    title: 'Reports',
    description: 'Business intelligence reports including revenue analysis and financial data',
    route: '/reports',
    icon: 'ReportDocument',
    department: 'reporting',
    permissions: ['reports.catalog'],
    sortOrder: 10,
  },
  {
    id: 'maintenanceSupportRevenue',
    title: 'Maintenance & Support Revenue',
    description: 'Analyze maintenance and support revenue by classification',
    route: '/reports/maintenance-support-revenue',
    icon: 'Money',
    department: 'reporting',
    permissions: ['reports.maintenanceSupportRevenue'],
    sortOrder: 20,
  },
  {
    id: 'saMetricsReport',
    title: 'SA Metrics Report',
    description: 'Solutions Architect activity metrics (HTML report viewer)',
    route: '/reports/sa-metrics',
    icon: 'ReportDocument',
    department: 'reporting',
    permissions: ['reports.saMetrics'],
    sortOrder: 25,
  },
  {
    id: 'cwSlaTicketMemberBreakdown',
    title: 'CW SLA Ticket Member Breakdown',
    description: 'SLA acknowledgment and resolution metrics by ticket owner and board',
    route: '/reports/cw-sla-ticket-member-breakdown',
    icon: 'ReportDocument',
    department: 'reporting',
    permissions: ['reports.slaTicket'],
    sortOrder: 26,
  },
  {
    id: 'cwSlaTroubleTicketMemberBreakdown',
    title: 'CW SLA Trouble Ticket Member Breakdown',
    description: 'Trouble-ticket SLA acknowledgment and completion by member and board',
    route: '/reports/cw-sla-trouble-ticket-member-breakdown',
    icon: 'ReportDocument',
    department: 'reporting',
    permissions: ['reports.slaTrouble'],
    sortOrder: 27,
  },
  {
    id: 'servicesEosReport',
    title: 'Services EOS Report',
    description: 'Services EOS SLA by board over a date range with Tiered and special-board totals',
    route: '/reports/services-eos',
    icon: 'ReportDocument',
    department: 'reporting',
    permissions: ['reports.servicesEos'],
    sortOrder: 27,
  },
  {
    id: 'psOpenProjectStatus',
    title: 'PS Open Project Status',
    description: 'Open Project/SOW tickets with age, completion, tasks, and budget vs actual hours',
    route: '/reports/ps-open-project-status',
    icon: 'ReportDocument',
    department: 'reporting',
    permissions: ['reports.psOpenProjectStatus'],
    sortOrder: 27.5,
  },
  {
    id: 'cwUserTimeSheetSummary',
    title: 'CW User Time Sheet Summary',
    description: 'Weekly approved time sheet hours by employee (regular, OT, PTO, leave)',
    route: '/reports/cw-user-time-sheet-summary',
    icon: 'ReportDocument',
    department: 'reporting',
    permissions: ['reports.timesheet'],
    sortOrder: 28,
  },
  {
    id: 'cwUserTimeSheetSummaryExport',
    title: 'CW User Time Sheet Summary For Export',
    description: 'Payroll export view of approved time sheet hours aggregated by employee',
    route: '/reports/cw-user-time-sheet-summary-export',
    icon: 'ReportDocument',
    department: 'reporting',
    permissions: ['reports.timesheetExport'],
    sortOrder: 29,
  },
  {
    id: 'experianExport',
    title: 'Experian Export',
    description: 'Export invoiced companies and full Experian files',
    route: '/experianexport',
    icon: 'ExcelDocument',
    department: 'reporting',
    permissions: ['experian.export'],
    sortOrder: 30,
  },
  {
    id: 'ntgoalDashboard',
    title: 'Nt Goal Dashboard',
    description: 'Track financial goals and ConnectWise opportunities',
    route: '/ntgoal-dashboard',
    icon: 'BarChartVertical',
    department: 'reporting',
    permissions: ['ntgoal.view'],
    sortOrder: 40,
  },
  {
    id: 'clientInformation',
    title: 'Client Information',
    description: 'Generate client PDFs and update ConnectWise company data from finance forms',
    route: '/clientinformation',
    icon: 'ContactCard',
    department: 'reporting',
    permissions: ['clientinformation.manage'],
    sortOrder: 50,
  },

  // Operations
  {
    id: 'inventory',
    title: 'Inventory Management',
    description: 'Track and manage company assets, equipment, and inventory',
    route: '/inventory',
    icon: 'Database',
    department: 'operations',
    permissions: ['inventory.view'],
    sortOrder: 10,
  },
  {
    id: 'purchaseRequest',
    title: 'Purchase Request',
    description: 'Submit purchase requests for manager approval',
    route: '/purchase-request',
    icon: 'ShoppingCart',
    department: 'operations',
    permissions: ['purchaserequest.submit'],
    sortOrder: 20,
  },
  {
    id: 'usacImport',
    title: 'USAC Import',
    description: 'Import and manage E-rate FRN data',
    route: '/usac-import',
    icon: 'Upload',
    department: 'operations',
    permissions: ['usac.use'],
    sortOrder: 30,
  },
  {
    id: 'platformMapping',
    title: 'Platform Mapping',
    description: 'Map Auvik, Datto, and SentinelOne sites to ConnectWise companies',
    route: '/platform-mapping',
    icon: 'Link',
    department: 'services',
    permissions: ['platformmapping.write'],
    sortOrder: 50,
  },
  {
    id: 'companyTeamCleanup',
    title: 'Company Team Cleanup',
    description: 'Remove members from ConnectWise company teams in bulk',
    route: '/company-team-cleanup',
    icon: 'PeopleBlock',
    department: 'operations',
    permissions: ['companyteamcleanup.use'],
    sortOrder: 50,
  },
  {
    id: 'configManagement',
    title: 'Config Management',
    description:
      'Find still-active configurations with closed lost renewals and mark them inactive in ConnectWise',
    route: '/config-management',
    icon: 'Blocked',
    department: 'operations',
    permissions: ['configmanagement.view'],
    sortOrder: 55,
  },

  // Tools
  {
    id: 'connectwiseTools',
    title: 'ConnectWise Tools',
    description: 'Administrative tools for ConnectWise data management',
    route: '/cwtools',
    icon: 'ConnectContacts',
    department: 'tools',
    toolsSubgroup: 'connectwise',
    permissions: ['cwtools.view'],
    sortOrder: 10,
  },
  {
    id: 'keyManagement',
    title: 'Key Management',
    description: 'Manage application secrets in Azure Key Vault',
    route: '/admin/key-management/secrets',
    icon: 'Lock',
    department: 'tools',
    toolsSubgroup: 'security',
    permissions: ['keyvault.admin'],
    sortOrder: 20,
  },
  {
    id: 'keyVaultPermissions',
    title: 'Key Vault Permissions',
    description: 'Manage Key Vault access permissions',
    route: '/admin/key-management/permissions',
    icon: 'Permissions',
    department: 'tools',
    toolsSubgroup: 'security',
    permissions: ['keyvault.admin'],
    sortOrder: 30,
  },
  {
    id: 'accessControl',
    title: 'Access Control',
    description: 'View roles, permission maps, and assign people in Entra',
    route: '/admin/rbac',
    icon: 'SecurityGroup',
    department: 'tools',
    toolsSubgroup: 'security',
    permissions: ['rbac.view'],
    sortOrder: 35,
  },
  {
    id: 'ntPass',
    title: 'NtPass Password Manager',
    description: 'Internal password management utility',
    route: '/ntpass',
    icon: 'Lock',
    department: 'tools',
    toolsSubgroup: 'security',
    permissions: ['ntpass.use'],
    sortOrder: 40,
  },
  {
    id: 'reportDocs',
    title: 'Reports Documentation',
    description: 'Report Server catalog with subscriptions and schedules',
    route: '/docs',
    icon: 'ReportDocument',
    department: 'tools',
    toolsSubgroup: 'documentation',
    permissions: ['reportdocs.view'],
    sortOrder: 50,
  },
  {
    id: 'reportSurvey',
    title: 'Report Usage Survey',
    description: 'Help prioritize report migrations by indicating which reports you use',
    route: '/report-survey',
    icon: 'Questionnaire',
    department: 'tools',
    toolsSubgroup: 'documentation',
    permissions: ['reportsurvey.submit'],
    sortOrder: 60,
  },
  {
    id: 'reportSurveyAdmin',
    title: 'Survey Administration',
    description: 'Administer report usage survey responses',
    route: '/report-survey-admin',
    icon: 'Admin',
    department: 'tools',
    toolsSubgroup: 'documentation',
    permissions: ['reportsurvey.admin'],
    sortOrder: 70,
  },

  // Communication
  {
    id: 'lokiAi',
    title: 'Loki AI Chat',
    description: 'Conversational AI assistant with customizable personalities',
    route: '/loki',
    icon: 'ChatBot',
    department: 'communication',
    permissions: ['loki.use'],
    quickAccess: true,
    sortOrder: 10,
  },
  {
    id: 'nettechChat',
    title: 'Nettech Chat',
    description: 'Shared internal chat for all authenticated users with 12-hour in-memory history',
    route: '/chat',
    icon: 'Comment',
    department: 'communication',
    permissions: ['chat.use'],
    quickAccess: true,
    sortOrder: 20,
  },
];
